import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

// Request tracing (part 37). The API records one row per request span under the W3C trace id
// the caller sent, so several requests that share a traceparent appear here together. This page
// is read-only: sampling and retention are server-side concerns.

interface TraceSpan {
  id: string
  trace_id: string
  parent_span?: string
  tenant_id?: string
  route: string
  method: string
  status: number
  dur_ms: number
  started_at: string
  meta?: Record<string, unknown>
}

interface TraceDetail {
  trace_id: string
  span_count: number
  spans: TraceSpan[]
}

function statusColor(status: number): string {
  if (status >= 500) return 'var(--color-crimson)'
  if (status >= 400) return 'var(--color-gold)'
  if (status >= 300) return 'var(--color-ivory-dim)'
  return 'var(--color-emerald-soft)'
}

function duration(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`
  return `${ms} ms`
}

function Waterfall({ spans }: { spans: TraceSpan[] }) {
  if (spans.length === 0) {
    return <Empty title="ไม่พบ span" body="trace นี้ไม่มีข้อมูลในช่วงเวลาที่เก็บไว้ อาจถูกตัดตามการเก็บรักษา" />
  }
  const ordered = [...spans].sort((a, b) => {
    const d = Date.parse(a.started_at) - Date.parse(b.started_at)
    return d !== 0 ? d : a.dur_ms - b.dur_ms
  })
  const t0 = Date.parse(ordered[0].started_at)
  const ends = ordered.map((s) => Date.parse(s.started_at) + Math.max(s.dur_ms, 1))
  const total = Math.max(Math.max(...ends) - t0, 1)
  const byId = new Map(ordered.map((s) => [s.id, s]))

  // Indent a span under its parent when the parent is part of this trace. The cap keeps a
  // corrupt cycle from turning the waterfall into an infinite hallway.
  const depthOf = (s: TraceSpan): number => {
    let depth = 0
    let cursor = s.parent_span
    while (cursor && byId.has(cursor) && depth < 4) {
      depth++
      cursor = byId.get(cursor)?.parent_span
    }
    return depth
  }

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="dim" style={{ fontSize: 11.5 }}>
          {ordered.length} span · ช่วงรวม {duration(total)}
        </span>
        <span className="dim tnum" style={{ fontSize: 11 }}>0 — {duration(total)}</span>
      </div>
      {ordered.map((s) => {
        const offset = Math.max(Date.parse(s.started_at) - t0, 0)
        const left = Math.min((offset / total) * 100, 99)
        const width = Math.max((Math.max(s.dur_ms, 1) / total) * 100, 0.8)
        const depth = depthOf(s)
        const requestID = typeof s.meta?.request_id === 'string' ? s.meta.request_id : ''
        return (
          <div key={s.id} style={{ paddingLeft: depth * 16 }}>
            <div className="row" style={{ justifyContent: 'space-between', gap: 12, marginBottom: 5 }}>
              <span className="mono" title={requestID ? `request ${requestID}` : undefined}
                style={{ fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <span className="dim">{s.method}</span> {s.route}
                {s.parent_span ? <span className="dim"> · ลูกของ {s.parent_span.slice(0, 6)}</span> : null}
              </span>
              <span className="dim tnum" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                +{offset} ms · {duration(s.dur_ms)} · {s.status}
              </span>
            </div>
            <div style={{
              position: 'relative', height: 9, borderRadius: 999,
              background: 'rgba(255,255,255,0.04)', overflow: 'hidden',
            }}>
              <i style={{
                position: 'absolute', top: 0, bottom: 0, left: `${left}%`, width: `${width}%`,
                background: statusColor(s.status), borderRadius: 999,
              }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function Traces() {
  const [selected, setSelected] = useState<string | null>(null)

  const recent = useQuery({
    queryKey: ['traces', 'recent'],
    queryFn: () => api<TraceSpan[]>('/v1/traces?limit=50'),
    staleTime: 0,
    refetchInterval: 15_000,
    retry: 0,
  })
  const slowest = useQuery({
    queryKey: ['traces', 'slowest'],
    queryFn: () => api<TraceSpan[]>('/v1/traces/slowest?limit=8'),
    staleTime: 0,
    refetchInterval: 30_000,
    retry: 0,
  })
  const detail = useQuery({
    queryKey: ['traces', 'detail', selected],
    queryFn: () => api<TraceDetail>(`/v1/traces/${selected}`),
    enabled: selected !== null,
    staleTime: 10_000,
    retry: 0,
  })

  const rows = recent.data ?? []
  const slowRows = slowest.data ?? []
  const errors = rows.filter((r) => r.status >= 500).length
  const distinct = new Set(rows.map((r) => r.trace_id)).size
  const worst = rows.reduce((max, r) => Math.max(max, r.dur_ms), 0)

  const toggle = (traceID: string) => setSelected((cur) => (cur === traceID ? null : traceID))
  const refresh = () => {
    void recent.refetch()
    void slowest.refetch()
    if (selected) void detail.refetch()
  }

  return (
    <>
      <PageHead
        title="การติดตามคำขอ"
        sub="ทุกคำขอที่บันทึกไว้ภายใต้ trace id เดียวกัน · อัปเดตอัตโนมัติทุก 15 วินาที"
        actions={
          <button className="btn btn-ghost btn-sm" onClick={refresh} disabled={recent.isFetching || slowest.isFetching}>
            {recent.isFetching || slowest.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
          </button>
        }
      />

      <div className="grid-4 stagger">
        <Stat label="คำขอล่าสุด" value={rows.length} unit="รายการ" foot="50 รายการล่าสุดขององค์กรนี้" />
        <Stat label="trace ที่แตกต่าง" value={distinct} unit="trace"
          foot="คำขอที่ส่ง traceparent เดียวกันจะรวมอยู่ที่นี่" />
        <Stat label="ข้อผิดพลาด 5xx" value={errors} unit="รายการ"
          foot={rows.length > 0 ? `${((errors / rows.length) * 100).toFixed(1)}% ของช่วงที่แสดง` : 'ยังไม่มีข้อมูล'} />
        <Stat label="ช้าที่สุดในช่วง" value={duration(worst)} unit="" foot="ดูอันดับด้านล่างได้" />
      </div>

      <div className="table-wrap card" style={{ marginTop: 18 }}>
        <table className="data">
          <thead>
            <tr>
              <th style={{ width: 150 }}>เวลา</th>
              <th style={{ width: 260 }}>Trace ID</th>
              <th style={{ width: 80 }}>Method</th>
              <th>Route</th>
              <th style={{ width: 80 }} className="num">สถานะ</th>
              <th style={{ width: 100 }} className="num">เวลา</th>
            </tr>
          </thead>
          <tbody>
            {recent.isLoading ? (
              <tr><td colSpan={6}><Skel h={90} /></td></tr>
            ) : recent.isError ? (
              <tr><td colSpan={6}>
                <Empty title="โหลด traces ไม่สำเร็จ"
                  body={recent.error instanceof Error ? recent.error.message : 'ลองรีเฟรชอีกครั้ง'} />
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6}>
                <Empty title="ยังไม่มีคำขอที่ถูกบันทึก"
                  body="ส่ง header traceparent มากับคำขอ หรือรอให้มีทราฟฟิกเข้าระบบ" />
              </td></tr>
            ) : rows.map((s) => {
              const open = selected === s.trace_id
              return (
                <tr key={s.id} onClick={() => toggle(s.trace_id)}
                  style={{ cursor: 'pointer', background: open ? 'rgba(200,169,106,0.06)' : undefined }}>
                  <td className="dim">{thaiDateTime(s.started_at)}</td>
                  <td>
                    <div className="row" style={{ gap: 7 }}>
                      <span style={{ color: 'var(--color-gold)', width: 10 }}>{open ? '▾' : '▸'}</span>
                      <span className="mono" title={s.trace_id} style={{ fontSize: 11.5 }}>
                        {s.trace_id.slice(0, 12)}…
                      </span>
                    </div>
                  </td>
                  <td className="mono dim">{s.method}</td>
                  <td className="mono" style={{ fontSize: 11.5 }}>{s.route}</td>
                  <td className="num tnum" style={{ color: statusColor(s.status), fontWeight: 600 }}>{s.status}</td>
                  <td className="num tnum">{duration(s.dur_ms)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {selected ? (
        <div className="card card-pad" style={{ marginTop: 14 }}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <div className="card-label" style={{ marginBottom: 6 }}>WATERFALL</div>
              <div className="mono" style={{ fontSize: 12, color: 'var(--color-gold)' }}>{selected}</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setSelected(null)}>ปิด</button>
          </div>
          {detail.isLoading ? <Skel h={120} />
            : detail.isError ? (
              <Empty title="โหลดรายละเอียด trace ไม่สำเร็จ"
                body={detail.error instanceof Error ? detail.error.message : 'trace อาจถูกเก็บรักษาไปแล้ว'} />
            ) : (
              <Waterfall spans={detail.data?.spans ?? []} />
            )}
        </div>
      ) : null}

      <div className="card card-pad" style={{ marginTop: 18 }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="card-label">คำขอที่ช้าที่สุด</div>
          <span className="dim" style={{ fontSize: 11.5 }}>8 อันดับ · เฉพาะ span ที่มีเวลามากกว่า 0</span>
        </div>
        {slowest.isLoading ? <Skel h={100} />
          : slowest.isError ? (
            <Empty title="โหลดอันดับความช้าไม่สำเร็จ"
              body={slowest.error instanceof Error ? slowest.error.message : 'ลองใหม่ภายหลัง'} />
          ) : slowRows.length === 0 ? (
            <Empty title="ยังไม่มีข้อมูลเวลา" body="เมื่อมีคำขอที่ใช้เวลามากกว่า 0 ms จะแสดงที่นี่" />
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 44 }} className="num">#</th>
                  <th>Route</th>
                  <th style={{ width: 80 }}>Method</th>
                  <th style={{ width: 90 }} className="num">สถานะ</th>
                  <th style={{ width: 110 }} className="num">เวลา</th>
                  <th style={{ width: 220 }}>Trace</th>
                </tr>
              </thead>
              <tbody>
                {slowRows.map((s, i) => (
                  <tr key={s.id} onClick={() => toggle(s.trace_id)}
                    style={{ cursor: 'pointer', background: selected === s.trace_id ? 'rgba(200,169,106,0.06)' : undefined }}>
                    <td className="num dim tnum">{i + 1}</td>
                    <td className="mono" style={{ fontSize: 11.5 }}>{s.route}</td>
                    <td className="mono dim">{s.method}</td>
                    <td className="num tnum" style={{ color: statusColor(s.status) }}>{s.status}</td>
                    <td className="num tnum" style={{ color: 'var(--color-gold)' }}>{duration(s.dur_ms)}</td>
                    <td>
                      <span className="mono dim" title={s.trace_id} style={{ fontSize: 11 }}>
                        {s.trace_id.slice(0, 16)}…
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <div className="dim" style={{ fontSize: 11.5, marginTop: 12, lineHeight: 1.7 }}>
        เซิร์ฟเวอร์อ่าน header <span className="mono">traceparent</span> ตามมาตรฐาน W3C แล้วบันทึกหนึ่ง span ต่อคำขอ ·
        การสุ่มตัวอย่างตัดสินจาก trace id (ค่าเริ่มต้น 1/1, ตั้ง <span className="mono">TRACE_SAMPLE_EVERY=10</span> เพื่อเก็บ 1 ใน 10) ·
        trace ที่ไม่มี tenant (เช่น /healthz) จะไม่แสดงในหน้านี้
      </div>
    </>
  )
}
